import React, { useState, useEffect, useContext, useCallback } from 'react';

import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { FlexProgramService } from 'wmic-pe-capability-gateway';
import { WMICCard, WMICAccordionCard } from 'wmic-pe-components-platform-react';
import { WMICErrorHandler, WMICProgressModal } from 'wmic-pe-capability-quoteandbind-common-react';
import { formatNumber, formatCurrency } from '@jutro/components';
import { InputField } from '@jutro/legacy/components';
import { useAccordionValidation } from 'wmic-pe-portals-wizard-components-ui';
import { DeviceBreakpoint, Position, JobType, parseErrorMessage, WMICLogger } from 'wmic-pe-portals-utils-js';
import _ from 'lodash';

import messages from './WMICIterableFlexRiskItem.messages';
import metadata from './WMICIterableFlexRiskItem.metadata.json5';
import styles from './WMICIterableFlexRiskItem.module.scss';

import { IntlContext, TranslatorContext } from '@jutro/legacy/locale';

const WMICIterableFlexRiskItem = props => {
    const {
        id,
        flexVM,
        updateFlexVM,
        riskHeader,
        riskType,
        onValidate,
        showErrors,
        viewModelService,
        readOnly,
        authHeader,
        modalVM,
    } = props;


    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);
    const isFlexEligible = flexVM?.isFlexEligible?.value;
    const labelPosition = DeviceBreakpoint.DESKTOP ? Position.LEFT : Position.TOP;
    const jobType = modalVM?.baseData?.jobType?.value?.code;

    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const { onValidateAccordion, isAccordionValid } = useAccordionValidation(onValidate);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }

        return () => {
            if (onValidate) {
                onValidate(true, id);
            }
        };
    }, [isComponentValid, onValidate, flexVM, id]);

    const updateFlexData = newFlexDTO => {
        updateFlexVM(viewModelService.clone(newFlexDTO));
    };

    const getRecalculatedFlexVM = useCallback(async (jobVM) => {
        let jobID;
        switch (jobType) {
            case JobType.SUBMISSION:
                jobID = jobVM.quoteID.value;
                break;
            case JobType.POLICY_CHANGE:
                jobID = jobVM.jobID.value;
                break;
            case JobType.RENEWAL:
                jobID = jobVM.jobID.value;
                break;
            default:
                break;
        }

        return FlexProgramService.recalculateFlexPreview(jobID, flexVM.value, authHeader);
    }, [jobType, authHeader, flexVM]);

    const areFlexFieldsEmpty = () => flexVM?.flexDollarAmount?.value == null && flexVM?.flexPercentage?.value == null && flexVM?.flexReasonDescription?.value == null && flexVM?.flexReason?.value == null;

    const flexPercentageFieldIsValid = () => (flexVM?.flexPercentage?.aspects?.valid || flexVM?.flexPercentage?.value === 0);

    const flexDollarAmountFieldIsValid = () => (flexVM?.flexDollarAmount?.aspects?.valid || flexVM?.flexDollarAmount?.value?.amount === 0);

    const calculateFlexPreview = () => {
        try {
            if (areFlexFieldsEmpty() ||
                (!flexPercentageFieldIsValid() && !flexDollarAmountFieldIsValid()) ||
                (flexPercentageFieldIsValid() && flexDollarAmountFieldIsValid())) {
                return;
            }

            updateFlexData(flexVM);
            setIsLoading(true);
            getRecalculatedFlexVM(modalVM).then((updatedFlexVM) => {
                if (updatedFlexVM) {
                    flexVM.flexDollarAmount.value = updatedFlexVM?.flexDollarAmount;
                    flexVM.flexPercentage.value = updatedFlexVM?.flexPercentage;
                    flexVM.flexTargetPremium.value = updatedFlexVM?.flexTargetPremium;
                    updateFlexData(flexVM);
                }
            }).catch((error) => {
                WMICLogger.error(translator(messages.errorRecalculatingFlexFields), error);
                WMICErrorHandler.processAsModal(`${translator(messages.errorRecalculatingFlexFields)}: ${parseErrorMessage(error)}`).catch(() => { });
            }).finally(() => {
                setIsLoading(false);
            });
        }
        catch (error) {
            WMICLogger.error(translator(messages.errorRecalculatingFlexFields), error);
            WMICErrorHandler.processAsModal(`${translator(messages.errorRecalculatingFlexFields)}: ${parseErrorMessage(error)}`).catch(() => { });
        }
    }

    const eligiblePremiumValue = isFlexEligible
        ? formatCurrency(intl, {
            value: _.get(flexVM, 'flexEligiblePremium.value', 0),
            showFractions: 2,
        })
        : translator(messages.inEligibleFlexPremium);

    const maxReductionAllowedValue = isFlexEligible
        ? formatCurrency(intl, {
            value: flexVM?.maxFlexAllowance?.value ?? 0,
            showFractions: 2,
        })
        : translator(messages.flexNotApplicable);

    const maxFlexPercentageValue = isFlexEligible
        ? formatNumber(intl, {
            value: (flexVM?.maxFlexPercentageAllowed_WMIC?.value ?? 0) / 100,
            format: 'percent',
        })
        : translator(messages.flexNotApplicable);

    const overrideProps = {
        '@field': {
            parentNode: flexVM,
        },
        flexFormContainer: {
            visible: isFlexEligible,
            onValidate: onValidateAccordion,
        },
        eligiblePremiumField: {
            value: eligiblePremiumValue,
        },
        maximumFlexPercentage: {
            value: maxFlexPercentageValue,
        },
        maxReductionField: {
            value: maxReductionAllowedValue,
        },
        flexErrorsContainer: {
            visible: flexVM.value.flexErrors.length > 0,
        },
        flexErrorsMessage: {
            content: _.get(flexVM, 'flexErrors.children.value')
                ? translator(messages.flexErrorsMessageYes, { riskType })
                : translator(messages.flexErrorsMessageNo, { riskType }),
        },
        flexErrorsList: {
            data: _.get(flexVM, 'flexErrors.children'),
        },
        flexReason: {
            readOnly,
            labelPosition,
        },
        flexReasonDescription: {
            readOnly,
            labelPosition,
            visible: _.get(flexVM, 'flexReasonDescription.aspects.ocular', false),
        },
        flexDollarAmount: {
            onValueChange: (value) => {
                // using a loose equality check due to potential undefined != null conditions
                if (value?.amount == flexVM?.flexDollarAmount?.value?.amount) {
                    return;
                }
                const newFlexVM = viewModelService.clone(flexVM);
                newFlexVM.flexDollarAmount.value = value.amount === null ? null : value;
                newFlexVM.flexPercentage.value = null;
                updateFlexVM(newFlexVM);
            },
            secondaryLabel: translator(messages.maxDiscountFlexDollarAmount, { dollarAmount: maxReductionAllowedValue }),
            readOnly
        },
        flexPercentage: {
            onValueChange: (value) => {
                const newFlexVM = viewModelService.clone(flexVM);
                newFlexVM.flexPercentage.value = value.amount === "" ? null : value;
                newFlexVM.flexDollarAmount.value = null;
                updateFlexVM(newFlexVM);
            },
            secondaryLabel: translator(messages.maxDiscountFlexPercentage, { percentage: maxFlexPercentageValue }),
            readOnly
        },
        estimateButton: {
            onClick: calculateFlexPreview,
            disabled: readOnly,
        },
        flexTargetPremium: {
            readOnly: true,
            labelPosition,
            visible: _.get(flexVM, 'flexTargetPremium.aspects.ocular', false),
        },
    };

    const resolvers = {
        resolveCallbackMap: {},
        resolveClassNameMap: styles,
        resolveComponentMap: {
            InputField,
        },
    };

    if (isLoading) {
        return <WMICProgressModal modalTitle={translator(messages.fetchingTitle)} isOpen={isLoading} />;
    }

    return (
        <WMICCard className={styles.flexRiskCard}>
            <WMICAccordionCard
                id={id}
                chevron
                className={styles.flexAccordionHeader}
                title={riskHeader}
                isValid={isComponentValid}
                showErrors={showErrors}
            >
                <ViewModelForm
                    uiProps={metadata.componentContent}
                    overrideProps={overrideProps}
                    classNameMap={resolvers.resolveClassNameMap}
                    componentMap={resolvers.resolveComponentMap}
                    model={flexVM}
                    onModelChange={updateFlexData}
                    onValidationChange={setComponentValidation}
                    showErrors={showErrors}
                />
            </WMICAccordionCard>
        </WMICCard>
    );
};

export default WMICIterableFlexRiskItem;
